import type {
  ChattingDataSource,
  CreateRoomBody,
  GetMessagesParams,
  SendMessageBody,
  UploadFileBody,
} from '~/api/data-source/chatting';
import { chattingDataSource } from '~/api/data-source/chatting';
import type {
  AlarmChatRoom,
  ChatMessage,
  ChatMessageList,
  ChatRoom,
  UploadFileResponse,
} from '~/api/model';
import {
  alarmChatRoomSchema,
  chatMessageListSchema,
  chatMessageSchema,
  chatRoomSchema,
  parseModel,
  parseModelArray,
  uploadFileResponseSchema,
} from '~/api/model';

// ============================================================================
// Repository Interface & Implementation
// ============================================================================

export interface ChattingRepository {
  /** 전체 읽지 않은 메시지 수 조회 */
  getTotalUnreadCount(): Promise<number>;

  /** 채팅 메시지 목록 조회 */
  getMessages(
    roomId: string,
    params: GetMessagesParams,
  ): Promise<ChatMessageList>;

  /** 메시지 전송 */
  sendMessage(body: SendMessageBody): Promise<ChatMessage>;

  /** 타이핑 이벤트 전송 */
  sendTyping(roomId: string, clientId: string): Promise<void>;

  /** 파일 업로드 (presigned URL 획득) */
  uploadFile(body: UploadFileBody): Promise<UploadFileResponse[]>;

  /** 알람 채팅방 목록 조회 */
  getAlarmChatRooms(): Promise<AlarmChatRoom[]>;

  /** 단일 알람 채팅방 조회 */
  getAlarmChatRoom(roomId: number): Promise<AlarmChatRoom>;

  /** 채팅방 푸시 알림 수신 여부 조회 */
  getPushEnabled(roomId: string): Promise<boolean>;

  /** 채팅방 푸시 알림 수신 여부 설정 */
  setPushEnabled(roomId: string, enabled: boolean): Promise<void>;

  /** 채팅방 생성 */
  createRoom(body: CreateRoomBody): Promise<ChatRoom>;
}

export class ChattingRepositoryImpl implements ChattingRepository {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly dataSource: ChattingDataSource = chattingDataSource,
  ) {}

  async getTotalUnreadCount() {
    const response = await this.dataSource.getTotalUnreadCount();
    return response.totalUnreadCount ?? 0;
  }

  async getMessages(roomId: string, params: GetMessagesParams) {
    const response = await this.dataSource.getMessages(roomId, params);
    // API가 배열을 직접 반환하는 경우 래핑
    const normalized = Array.isArray(response)
      ? { messages: response }
      : response;
    return parseModel(chatMessageListSchema, normalized, 'ChatMessageList');
  }

  async sendMessage(body: SendMessageBody) {
    const response = await this.dataSource.sendMessage(body);
    return parseModel(chatMessageSchema, response, 'ChatMessage');
  }

  async sendTyping(roomId: string, clientId: string) {
    await this.dataSource.sendTyping(roomId, clientId);
  }

  async uploadFile(body: UploadFileBody) {
    const response = await this.dataSource.uploadFile(body);
    return parseModelArray(
      uploadFileResponseSchema,
      response,
      'UploadFileResponse',
    );
  }

  async getAlarmChatRooms() {
    const response = await this.dataSource.getAlarmChatRooms();
    return parseModelArray(alarmChatRoomSchema, response, 'AlarmChatRoom');
  }

  async getAlarmChatRoom(roomId: number) {
    const response = await this.dataSource.getAlarmChatRoom(roomId);
    return parseModel(alarmChatRoomSchema, response, 'AlarmChatRoom');
  }

  async getPushEnabled(roomId: string) {
    const response = await this.dataSource.getPushEnabled(roomId);
    return response.pushEnabled ?? false;
  }

  async setPushEnabled(roomId: string, enabled: boolean) {
    await this.dataSource.setPushEnabled(roomId, enabled);
  }

  async createRoom(body: CreateRoomBody) {
    const response = await this.dataSource.createRoom(body);
    return parseModel(chatRoomSchema, response, 'ChatRoom');
  }
}

export const chattingRepository = new ChattingRepositoryImpl();
